import React, { useState } from 'react';
import { useStore } from '../context/StoreContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import OnboardingTaskRow from '../components/OnboardingTaskRow';
import { format } from 'date-fns';

const { FiPlus, FiZap, FiCheckCircle, FiClock, FiSearch, FiArrowRight, FiActivity, FiX } = FiIcons;

const DEFAULT_TASKS = [
  { task: 'Send Welcome Email & Access Instructions', category: 'Communication' },
  { task: 'Prepare Welcome Pack (keys, WiFi, guide)', category: 'Hospitality' },
  { task: 'Pre-Move-In Property Inspection', category: 'Inspection' },
  { task: 'Document Property Condition (photos)', category: 'Inspection' },
  { task: 'Verify Security Deposit Received', category: 'Financial' },
  { task: 'Confirm First Month Rent Payment', category: 'Financial' },
  { task: 'Set Up Utility Accounts / Transfer', category: 'Utilities' },
  { task: 'Test All Appliances & Systems', category: 'Inspection' },
  { task: 'Stock Essential Supplies', category: 'Hospitality' },
  { task: 'Schedule Move-In Walkthrough', category: 'Communication' },
];

const InitializeMoveInModal = ({ isOpen, onClose, onSave, leases, tenants, properties }) => {
  const [selectedLeaseId, setSelectedLeaseId] = useState('');
  const [customTasks, setCustomTasks] = useState(DEFAULT_TASKS.map((t, i) => ({ ...t, id: `task_${i}`, included: true })));
  const [newTask, setNewTask] = useState('');

  if (!isOpen) return null;

  const activeLeases = leases.filter(l => l.status === 'Active' || l.status === 'Signed');

  const addCustomTask = () => {
    if (!newTask.trim()) return;
    setCustomTasks(prev => [...prev, { id: `task_${Date.now()}`, task: newTask.trim(), category: 'Custom', included: true }]);
    setNewTask('');
  };

  const toggleTask = (id) => {
    setCustomTasks(prev => prev.map(t => t.id === id ? { ...t, included: !t.included } : t));
  };

  const removeTask = (id) => {
    setCustomTasks(prev => prev.filter(t => t.id !== id));
  };

  const handleSubmit = () => {
    if (!selectedLeaseId) return;
    const items = customTasks.filter(t => t.included).map((t, i) => ({
      id: `item_${Date.now()}_${i}`,
      task: t.task,
      category: t.category,
      completed: false,
      date: ''
    }));
    onSave({ leaseId: selectedLeaseId, status: 'In Progress', items });
    setSelectedLeaseId('');
    setCustomTasks(DEFAULT_TASKS.map((t, i) => ({ ...t, id: `task_${i}`, included: true })));
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h3 className="text-lg font-bold text-gray-900">Initialize Move-In</h3>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <SafeIcon icon={FiX} size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Select Lease *</label>
            <select required value={selectedLeaseId} onChange={e => setSelectedLeaseId(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none">
              <option value="">Choose a lease...</option>
              {activeLeases.map(l => {
                const tenant = tenants.find(t => t.id === l.tenantId);
                const prop = properties.find(p => p.id === l.propertyId);
                return <option key={l.id} value={l.id}>{tenant?.name || 'Unknown'} — {prop?.name || 'Unknown'}</option>;
              })}
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-500 uppercase">Onboarding Checklist</label>
              <span className="text-[10px] text-indigo-600 font-semibold">{customTasks.filter(t => t.included).length} tasks selected</span>
            </div>
            <div className="space-y-1.5 max-h-[250px] overflow-y-auto">
              {customTasks.map(t => (
                <div key={t.id} className={`flex items-center justify-between p-2.5 rounded-lg border transition-colors ${t.included ? 'bg-indigo-50/50 border-indigo-100' : 'bg-gray-50 border-gray-100 opacity-50'}`}>
                  <label className="flex items-center gap-2.5 cursor-pointer flex-1 min-w-0">
                    <input type="checkbox" checked={t.included} onChange={() => toggleTask(t.id)}
                      className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                    <div className="min-w-0">
                      <p className="text-sm text-gray-800 truncate">{t.task}</p>
                      <p className="text-[10px] text-gray-400">{t.category}</p>
                    </div>
                  </label>
                  {t.category === 'Custom' && (
                    <button onClick={() => removeTask(t.id)} className="p-1 text-gray-300 hover:text-red-500 shrink-0">
                      <SafeIcon icon={FiX} size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-2 mt-3">
              <input value={newTask} onChange={e => setNewTask(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomTask(); } }}
                className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none" placeholder="Add custom task..." />
              <button type="button" onClick={addCustomTask}
                className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200">Add</button>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3 shrink-0">
          <button onClick={onClose} className="flex-1 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
          <button onClick={handleSubmit} disabled={!selectedLeaseId}
            className="flex-1 py-2.5 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            Create Onboarding
          </button>
        </div>
      </div>
    </div>
  );
};

const Onboarding = () => {
  const { data, addRecord, updateRecord } = useStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const activeOnboardings = (data.onboardingChecklists || []).map(ob => {
    const lease = data.leases.find(l => l.id === ob.leaseId);
    const tenant = data.tenants.find(t => t.id === lease?.tenantId);
    const property = data.properties.find(p => p.id === lease?.propertyId);
    const completed = ob.items.filter(i => i.completed).length;
    const total = ob.items.length;
    const progress = Math.round((completed / total) * 100);
    
    return { ...ob, lease, tenant, property, progress, completed, total };
  }).filter(ob => 
    ob.tenant?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ob.property?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggleTask = (obId, taskId) => {
    const ob = data.onboardingChecklists.find(o => o.id === obId);
    if (!ob) return;
    
    const updatedItems = ob.items.map(item => {
      if (item.id === taskId) {
        return { 
          ...item, 
          completed: !item.completed,
          date: !item.completed ? format(new Date(), 'yyyy-MM-dd') : ''
        };
      }
      return item;
    });

    const allDone = updatedItems.every(i => i.completed);
    updateRecord('onboardingChecklists', obId, { 
      items: updatedItems,
      status: allDone ? 'Completed' : 'In Progress'
    });
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">Move-In Orchestrator</h2>
          <p className="text-sm text-gray-500 font-medium italic">Ensuring every midterm stay starts with perfection.</p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center px-5 py-3 bg-indigo-600 text-white rounded-2xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 font-black text-sm uppercase tracking-wider"
        >
          <SafeIcon icon={FiPlus} className="mr-2" /> Initialize Move-In
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Active Onboardings</p>
          <p className="text-3xl font-black text-gray-900">{activeOnboardings.filter(o => o.status !== 'Completed').length}</p>
        </div>
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Avg. Completion</p>
          <div className="flex items-end gap-2">
            <p className="text-3xl font-black text-indigo-600">72%</p>
            <span className="text-[10px] text-green-600 font-bold mb-1">+5% vs last month</span>
          </div>
        </div>
        <div className="md:col-span-2 bg-indigo-600 p-6 rounded-3xl text-white shadow-lg flex items-center justify-between">
          <div>
            <h3 className="font-black text-lg">Hospitality Standard</h3>
            <p className="text-xs text-indigo-100 font-medium">92% of "Welcome Packs" delivered on time.</p>
          </div>
          <SafeIcon icon={FiZap} size={40} className="text-indigo-400 opacity-50" />
        </div>
      </div>

      <div className="relative">
        <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
        <input 
          type="text" 
          placeholder="Filter by tenant or property..." 
          className="w-full pl-12 pr-4 py-3 bg-white border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-medium shadow-sm"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {activeOnboardings.map(ob => (
          <div key={ob.id} className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-50 bg-gray-50/30 flex justify-between items-start">
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-indigo-600 text-white rounded-2xl flex items-center justify-center font-black text-lg shadow-lg shadow-indigo-100">
                  {ob.tenant?.name?.[0] || '?'}
                </div>
                <div>
                  <h3 className="font-black text-gray-900 leading-tight">{ob.tenant?.name}</h3>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">{ob.property?.name}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs font-black text-indigo-600">{ob.progress}% Complete</p>
                <div className="w-24 bg-gray-200 h-1.5 rounded-full mt-2 overflow-hidden">
                  <div className="bg-indigo-600 h-full transition-all duration-1000" style={{ width: `${ob.progress}%` }} />
                </div>
              </div>
            </div>

            <div className="p-6 space-y-3 flex-1">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Onboarding Workflow</p>
                <div className="flex gap-1">
                  <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 text-[8px] font-black rounded uppercase">
                    {ob.completed}/{ob.total} Tasks
                  </span>
                </div>
              </div>
              {ob.items.map(item => (
                <OnboardingTaskRow 
                  key={item.id} 
                  item={item} 
                  onToggle={(taskId) => handleToggleTask(ob.id, taskId)} 
                />
              ))}
            </div>

            <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
              <div className="flex items-center gap-2 text-[10px] text-gray-400 font-bold uppercase">
                <SafeIcon icon={FiActivity} className="text-indigo-400" /> System: Move-in {ob.lease?.startDate}
              </div>
              <button className="text-[10px] font-black text-indigo-600 uppercase flex items-center gap-1 hover:underline">
                View Full Lease <SafeIcon icon={FiArrowRight} />
              </button>
            </div>
          </div>
        ))}

        {activeOnboardings.length === 0 && (
          <div className="col-span-full py-24 text-center bg-white rounded-3xl border border-dashed border-gray-200">
            <SafeIcon icon={FiCheckCircle} size={48} className="text-gray-100 mx-auto mb-4" />
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">No active onboarding workflows</p>
          </div>
        )}
      </div>

      <InitializeMoveInModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={(checklist) => addRecord('onboardingChecklists', checklist)}
        leases={data.leases || []}
        tenants={data.tenants || []}
        properties={data.properties || []}
      />
    </div>
  );
};

export default Onboarding;